"use client";

import Link from "next/link";
import { useOptimistic, useTransition } from "react";

import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UI_LOCALES } from "@/config/locales";
import { routes } from "@/config/routes";
import { THEMES } from "@/config/themes";
import { logoutAllAction, updateLocaleAction } from "@/features/auth/actions";
import { useCurrentUser } from "@/features/auth/components/current-user-provider";

/** Read-only for now: there's no backend endpoint to update the profile yet. */
export function AccountSection() {
  const user = useCurrentUser();
  const t = useTranslations("settings");

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor="settings-name">{t("name")}</FieldLabel>
        <Input id="settings-name" value={user.name} readOnly />
      </Field>
      <Field>
        <FieldLabel htmlFor="settings-email">{t("email")}</FieldLabel>
        <Input id="settings-email" type="email" value={user.email} readOnly />
        <FieldDescription>{t("contactAdmin")}</FieldDescription>
      </Field>
      <section aria-labelledby="settings-permissions" className="flex flex-col gap-2">
        <h3 id="settings-permissions" className="text-sm font-medium">
          {t("permissions")}
        </h3>
        {user.permissions.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {user.permissions.map((permission) => (
              <li key={permission}>
                <Badge variant="outline" className="font-mono">
                  {permission}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t("noPermissions")}</p>
        )}
      </section>
    </FieldGroup>
  );
}

/** The UI and email language (docs/specs/foundation/i18n.md rule 1). Saved to the account. */
export function LanguageSection() {
  const locale = useLocale();
  const t = useTranslations();
  const [isPending, startTransition] = useTransition();
  // Shows the choice right away; the app re-renders in it when the action returns
  const [selected, setSelected] = useOptimistic(locale);

  function change(value: string) {
    const next = UI_LOCALES.find((l) => l === value);
    if (!next || next === locale) return;
    startTransition(async () => {
      setSelected(next);
      const result = await updateLocaleAction(next);
      // On success the whole app re-renders in the new language: that's the confirmation
      if (!result.ok) toast.error(result.error);
    });
  }

  return (
    <FieldSet disabled={isPending}>
      <FieldLegend variant="label">{t("settings.languageLegend")}</FieldLegend>
      <FieldDescription>{t("settings.languageDescription")}</FieldDescription>
      <RadioGroup value={selected} onValueChange={change} className="sm:grid-cols-2">
        {UI_LOCALES.map((value) => (
          <FieldLabel key={value} htmlFor={`locale-${value}`}>
            <Field orientation="horizontal">
              <FieldContent>
                <FieldTitle>{t(`locales.${value}`)}</FieldTitle>
              </FieldContent>
              <RadioGroupItem value={value} id={`locale-${value}`} />
            </Field>
          </FieldLabel>
        ))}
      </RadioGroup>
    </FieldSet>
  );
}

export function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  const t = useTranslations();

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("settings.themeLegend")}</FieldLegend>
      <FieldDescription>{t("settings.themeDescription")}</FieldDescription>
      <RadioGroup value={theme ?? "system"} onValueChange={setTheme} className="sm:grid-cols-3">
        {THEMES.map(({ value, icon: Icon }) => (
          <FieldLabel key={value} htmlFor={`theme-${value}`}>
            <Field orientation="horizontal">
              <Icon aria-hidden className="size-4" />
              <FieldContent>
                <FieldTitle>{t(`themes.${value}`)}</FieldTitle>
              </FieldContent>
              <RadioGroupItem value={value} id={`theme-${value}`} />
            </Field>
          </FieldLabel>
        ))}
      </RadioGroup>
    </FieldSet>
  );
}

export function SecuritySection() {
  const [isPending, startTransition] = useTransition();
  const t = useTranslations();

  return (
    <FieldGroup>
      <Field orientation="responsive">
        <FieldContent>
          <FieldTitle>{t("settings.manageSecurity")}</FieldTitle>
          <FieldDescription>{t("settings.manageSecurityDescription")}</FieldDescription>
        </FieldContent>
        <Button variant="outline" asChild>
          <Link href={routes.security()}>{t("settings.openSecurity")}</Link>
        </Button>
      </Field>
      <Field orientation="responsive">
        <FieldContent>
          <FieldTitle>{t("settings.signOutEverywhere")}</FieldTitle>
          <FieldDescription>{t("settings.signOutEverywhereDescription")}</FieldDescription>
        </FieldContent>
        <Button
          variant="destructive"
          disabled={isPending}
          onClick={() => startTransition(() => logoutAllAction())}
        >
          {isPending ? t("shell.signingOut") : t("settings.signOutEverywhere")}
        </Button>
      </Field>
    </FieldGroup>
  );
}
